// src/hooks/useMobileAuth.js
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Custom hook to handle mobile-to-web authentication
 * Detects authToken in URL, verifies it server-side, and signs in the user automatically
 */
export const useMobileAuth = () => {
  const location = useLocation();
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    // Clean URL helper function
    const cleanUrl = () => {
      const newSearchParams = new URLSearchParams(window.location.search);
      newSearchParams.delete('authToken');
      
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch 
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      
      window.history.replaceState({}, '', newUrl);
    };

    // Use window.location.search as primary source - more reliable on initial load
    // useSearchParams might not be ready immediately, especially on first render
    const urlSearchParams = new URLSearchParams(window.location.search);
    const authToken = urlSearchParams.get('authToken');
    
    // Debug logging - always log to help diagnose issues
    console.log('[MobileAuth] Effect running:', {
      hasToken: !!authToken,
      tokenLength: authToken ? authToken.length : 0,
      tokenPreview: authToken ? `${authToken.substring(0, 20)}...` : null,
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      user: user ? user.uid : null,
      hasAttempted: hasAttemptedRef.current,
      timestamp: new Date().toISOString()
    });

    // If no token in URL, reset attempt flag and do nothing
    if (!authToken) {
      hasAttemptedRef.current = false;
      return;
    }

    // If user is already authenticated, just clean up the URL
    // But only if they're the same user (don't interrupt if they're already logged in as someone else)
    if (user) {
      console.log('[MobileAuth] User already authenticated, cleaning URL');
      cleanUrl();
      hasAttemptedRef.current = false; // Reset so it can work if token changes
      return;
    }

    // Prevent duplicate attempts
    if (hasAttemptedRef.current) {
      return;
    }

    // Mark that we're attempting authentication
    hasAttemptedRef.current = true;
    setLoading(true);
    setError(null);
    setSuccess(false);

    const handleMobileAuth = async () => {
      try {
        console.log('[MobileAuth] Detected authToken in URL, starting authentication');

        // Determine API URL (same pattern as email.js)
        const hostname = window.location.hostname;
        const port = window.location.port;
        const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
        const isDevelopment = hostname === 'localhost' || 
                              hostname === '127.0.0.1' || 
                              isLocalNetwork ||
                              port === '3000' ||
                              process.env.NODE_ENV === 'development';
        
        // Build API URL - API_BASE_URL already includes /api, so we just append the endpoint path
        // Construct API URL correctly handling /api prefix
        // Express route is /api/auth/mobile-signin (see functions/index.js:358)
        // Cloud Function base is ...cloudfunctions.net/api
        // Full URL needed: ...cloudfunctions.net/api/api/auth/mobile-signin
        let apiUrl;
        if (API_BASE_URL) {
          // If API_BASE_URL is set, check if it ends with /api
          const baseUrlEndsWithApi = API_BASE_URL.endsWith('/api');
          apiUrl = baseUrlEndsWithApi
            ? `${API_BASE_URL}/api/auth/mobile-signin`  // ...cloudfunctions.net/api/api/auth/mobile-signin
            : `${API_BASE_URL}/api/auth/mobile-signin`; // Custom domain: .../api/auth/mobile-signin
        } else if (isDevelopment) {
          apiUrl = 'http://localhost:4000/api/auth/mobile-signin';
        } else {
          // Production: Cloud Functions URL = ...cloudfunctions.net/api, Express route = /api/auth/mobile-signin
          // Full URL: ...cloudfunctions.net/api/api/auth/mobile-signin
          apiUrl = 'https://us-central1-epiphoric-prod.cloudfunctions.net/api/api/auth/mobile-signin';
        }

        console.log('[MobileAuth] Calling backend:', apiUrl);

        // Call backend to verify token and get custom token
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ idToken: authToken }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to authenticate');
        }

        if (!data.customToken) {
          throw new Error('No custom token received from server');
        }

        console.log('[MobileAuth] Custom token received, signing in user');

        // Sign in with the custom token
        await signInWithCustomToken(auth, data.customToken);

        console.log('[MobileAuth] User signed in successfully');
        
        setSuccess(true);
        
        // Clean up URL (remove authToken parameter)
        cleanUrl();
        
      } catch (err) {
        console.error('[MobileAuth] Authentication error:', {
          message: err.message,
          stack: err.stack,
          name: err.name,
          url: window.location.href
        });
        setError(err.message || 'Failed to sign in automatically. Please sign in manually.');
        
        // Clean up URL even on error (for security)
        cleanUrl();
      } finally {
        setLoading(false);
      }
    };

    handleMobileAuth();
  }, [location.search, user]);

  return { loading, error, success };
};

